import type { PlanType } from '@otter-ai/recall-client';
import { NEW_ONBOARDING_FLOW } from '@/shared/types/welcome';
import {
  getPartialLoginState,
  setPartialLoginState,
} from '@/shared/utils/auth';

function getOnboardingParams(
  planType: PlanType,
  experimentGroup?: string,
): Record<string, string> {
  switch (experimentGroup) {
    case 'treatment_b':
    case 'treatment_c':
      // New post-purchase flows
      return planType === 'premium'
        ? { new_onboarding: NEW_ONBOARDING_FLOW.PRO_POST_PURCHASE }
        : { new_onboarding: NEW_ONBOARDING_FLOW.BIZ_POST_PURCHASE };
    default:
      // Original logic
      return planType === 'premium'
        ? { new_onboarding: NEW_ONBOARDING_FLOW.GENERAL_EMAIL }
        : { new_onboarding: NEW_ONBOARDING_FLOW.WORKSPACE_ADMIN };
  }
}

function buildOnboardingUrl(
  planType: PlanType,
  experimentGroup?: string,
): string {
  const params = getOnboardingParams(planType, experimentGroup);
  const queryString = Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');
  return `/welcome${queryString ? `?${queryString}` : ''}`;
}

type HandlePurchaseSuccessRedirectOptions = {
  /**
   * Whether there is an active recording in progress.
   * If true, opens onboarding in a new tab instead of redirecting.
   */
  isRecording?: boolean;
  /**
   * The experiment group for post_purchase_onboarding_flow experiment.
   * Used to determine which onboarding flow to use.
   */
  experimentGroup?: string;
};

/**
 * Handles the redirect after a successful purchase.
 *
 * @param planType - The plan type that was purchased
 * @param options - Optional configuration
 * @param options.isRecording - If true, opens onboarding in a new tab instead of redirecting
 * @param options.experimentGroup - The post_purchase_onboarding_flow experiment group
 */
export function handlePurchaseSuccessRedirect(
  planType: PlanType,
  options: HandlePurchaseSuccessRedirectOptions = {},
): void {
  const { isRecording = false, experimentGroup } = options;
  const onboardingRedirectUrl = buildOnboardingUrl(planType, experimentGroup);
  const onboardingParams = getOnboardingParams(planType, experimentGroup);

  // Store the current URL in redirectQueryParams.r so the user
  // is redirected back after onboarding completes
  const currentUrl = window.location.pathname + window.location.search;

  setPartialLoginState({
    redirectUrl: onboardingRedirectUrl,
    redirectQueryParams: {
      ...getPartialLoginState().redirectQueryParams,
      ...onboardingParams,
      r: currentUrl,
    },
  });

  if (isRecording) {
    // Open onboarding in a new tab if user is in a live recording
    window.open(onboardingRedirectUrl, '_blank');
  } else {
    // Redirect in the same tab
    window.location.href = onboardingRedirectUrl;
  }
}
